import { createBrowserRouter, Outlet, ScrollRestoration } from 'react-router';
import { RequireReady, RequireRole, RequireSession } from '../features/auth/guards';
import { About } from '../routes/About';
import { AuthCallback } from '../routes/AuthCallback';
import { Compose } from '../routes/Compose';
import { Guidelines } from '../routes/Guidelines';
import { Home } from '../routes/Home';
import { Me } from '../routes/Me';
import { NotFound } from '../routes/NotFound';
import { Notices } from '../routes/Notices';
import { OnboardingName } from '../routes/OnboardingName';
import { Privacy } from '../routes/Privacy';
import { ProfilePage } from '../routes/ProfilePage';
import { Terms } from '../routes/Terms';
import { Thread } from '../routes/Thread';
import { VerifyLocationPage } from '../routes/VerifyLocationPage';
import { Welcome } from '../routes/Welcome';
import { AppShell } from './AppShell';
import { RouteError } from './RouteError';

function Root() {
  return (
    <>
      <ScrollRestoration />
      <Outlet />
    </>
  );
}

export const router = createBrowserRouter([
  {
    element: <Root />,
    errorElement: <RouteError />,
    children: [
      { path: '/welcome', element: <Welcome /> },
      { path: '/about', element: <About /> },
      { path: '/privacy', element: <Privacy /> },
      { path: '/guidelines', element: <Guidelines /> },
      { path: '/terms', element: <Terms /> },
      // Yard-sign QR codes: /at/golder-ranch shows the landing page, and the path
      // shows up in analytics (which ignores query strings) so we can tell which signs work.
      { path: '/at/:sign', element: <Welcome /> },
      { path: '/auth/callback', element: <AuthCallback /> },
      {
        element: <RequireSession />,
        children: [
          { path: '/onboarding/name', element: <OnboardingName /> },
          { path: '/onboarding/location', element: <VerifyLocationPage mode="onboarding" /> },
          {
            element: <RequireReady />,
            children: [
              {
                element: <AppShell />,
                children: [
                  { index: true, element: <Home /> },
                  { path: '/notices', element: <Notices /> },
                  { path: '/post/new', element: <Compose /> },
                  { path: '/p/:postId', element: <Thread /> },
                  { path: '/u/:userId', element: <ProfilePage /> },
                  { path: '/me', element: <Me /> },
                  { path: '/me/location', element: <VerifyLocationPage mode="change" /> },
                  {
                    element: <RequireRole role="moderator" />,
                    children: [
                      {
                        path: '/mod',
                        lazy: async () => ({ Component: (await import('../routes/mod/ModQueue')).ModQueue }),
                      },
                      {
                        path: '/mod/log',
                        lazy: async () => ({ Component: (await import('../routes/mod/ModLog')).ModLog }),
                      },
                      {
                        path: '/mod/members',
                        lazy: async () => ({ Component: (await import('../routes/mod/ModMembers')).ModMembers }),
                      },
                    ],
                  },
                  {
                    element: <RequireRole role="admin" />,
                    children: [
                      {
                        path: '/admin/geo',
                        lazy: async () => ({ Component: (await import('../routes/admin/GeoDebug')).GeoDebug }),
                      },
                    ],
                  },
                  { path: '*', element: <NotFound /> },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
]);
